import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import UserPersonalDetails from "../components/UserPersonalDetails";
import UserShortOverview from "../components/UserShortOverview";
import { UserCertificatesTable } from "../components/UserCertificatesTable";
import { AxisLineSeparator } from "../components/AxisLineSeparator";
import {
  ButtonActivateUser,
  ButtonBlockUser,
  ButtonDeleteUser,
  ButtonEditUser,
  ButtonOnHoldUser,
  ButtonUnBlockUser,
  ButtonUpdatePassword,
} from "../components/ShiftShiftButtonActionsElements";
import UserCreateUpdateModal from "../modals/UserCreateUpdateModal";
import PlaceOnHoldModal from "../modals/PlaceOnHoldModal";
import UpdatePasswordModal from "../modals/UpdatePasswordModal";
import UserProfessionalRoleSelectionModal from "../modals/UserProfessionalRoleSelectionModal";
import { PageTitleController } from "../components/PageTitleController";
import { BsPeopleFill } from "react-icons/bs";
import UserShiftHistory from "../components/UserShiftHistory";
import UserShiftCancels from "../components/UserShiftCancels";
import AvailabilityTable from "./AvailabilityTable";
import InvoiceTable from "./InvoiceTable";
import { UserNotes } from "../components/UserNotes";
import UserReferences from "../components/UserReferences";
import { isAdminRole } from "../util/Permissions";

const UserDetail = () => {
  const { authAxios } = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [key, setKey] = useState("personal");
  const [showUserEdit, setShowUserEdit] = useState(false);
  const [showHoldUser, setShowHoldUser] = useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);
  const [showRolePrompt, setShowRolePrompt] = useState(false);
  const [rolePromptDismissed, setRolePromptDismissed] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);

  const userMissingProfessionalRole = (targetUser) => {
    if (targetUser?.role !== "USER") return false;
    const selectedRoles = targetUser?.roles?.trim();
    return (
      selectedRoles === null ||
      selectedRoles === undefined ||
      selectedRoles === "" ||
      selectedRoles === "N/A"
    );
  };

  useEffect(() => {
    document.title = "User Detail";
    fetchNurseDetail();
    fetchCurrentUser();
    setRolePromptDismissed(false);
  }, [userId]);

  useEffect(() => {
    if (
      key === "references" &&
      userMissingProfessionalRole(user) &&
      !rolePromptDismissed
    ) {
      setShowRolePrompt(true);
    }
  }, [key, user, rolePromptDismissed]);


  const fetchCurrentUser = async () => {
    try {
      const response = await authAxios().get("/api/backoffice/users/current_user");
      setCurrentUserRole(response?.data?.role || null);
    } catch (error) {
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const fetchNurseDetail = async () => {
    try {
      setLoading(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          user_id: userId,
        },
      };

      const response = await authAxios().get(
        "/api/backoffice/users/detail",
        config,
      );

      setUser(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const unblockUser = async () => {
    try {
      setLoading(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          user_id: userId,
        },
      };

      const response = await authAxios().post(
        "/api/backoffice/users/unblock_user",
        null,
        config,
      );

      setUser(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const blockUser = async () => {
    try {
      setLoading(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          user_id: userId,
        },
      };

      const response = await authAxios().post(
        "/api/backoffice/users/block_user",
        null,
        config,
      );

      setUser(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const activateUser = async () => {
    try {
      setLoading(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          user_id: userId,
        },
      };

      const response = await authAxios().post(
        "/api/backoffice/users/activate_user",
        null,
        config,
      );

      setUser(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const deleteUser = async () => {
    try {
      setLoading(true);

      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {
          user_id: userId,
        },
      };

      await authAxios().delete(
        "/api/backoffice/users/delete_pending_user",
        config,
      );

      // Navigate to users list
      navigate("/users");
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
  };

  const closeRolePrompt = () => {
    setRolePromptDismissed(true);
    setShowRolePrompt(false);
  };

  return (
    <div>
      <Row className={"icon-text-wrapper"}>
        <PageTitleController
          title={"User"}
          icon={<BsPeopleFill className={"icon-right-spacing"} />}
        />
        <Col>
          <Row
            xs={"auto"}
            className={"justify-content-end"}
            style={{ marginTop: "-32px" }}
          >
            <ButtonUpdatePassword action={() => setShowUpdatePassword(true)} />
            <ButtonActivateUser
              user={user}
              action={() => {
                activateUser();
              }}
            />
            <ButtonDeleteUser
              user={user}
              action={() => {
                if (window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
                  deleteUser();
                }
              }}
            />
            <ButtonOnHoldUser
              user={user}
              action={() => setShowHoldUser(true)}
            />
            <ButtonUnBlockUser
              user={user}
              action={() => {
                unblockUser();
              }}
            />
            <ButtonBlockUser
              user={user}
              action={() => {
                blockUser();
              }}
            />
            <ButtonEditUser action={() => setShowUserEdit(true)} />
          </Row>
        </Col>
      </Row>

      <AxisLineSeparator />

      <div>
        <UserShortOverview user={user} />
      </div>

      <div>
        <Tabs
          id="user-detail-table-tabs"
          activeKey={key}
          onSelect={(k) => setKey(k)}
          className="mb-3 axis-table-tabs"
        >
          <Tab eventKey="personal" title="Personal Info">
            <UserPersonalDetails
              user={user}
              onSelectProfessionalRole={() => setShowRolePrompt(true)}
            />
          </Tab>

          {user?.role === "USER" ? (
            <Tab eventKey="certs" title="Training">
              <UserCertificatesTable
                selectedUser={user}
                defaultSortKey="certificate"
                preserveSortHistory={false}
              />
            </Tab>
          ) : null}

          {user?.role === "USER" ? (
            <Tab eventKey="bookings" title="Shift History">
              <UserShiftHistory user={user} />
              {/*<TimesheetTable*/}
              {/*  defaultUser={user}*/}
              {/*  needTitle={false}*/}
              {/*  needTabs={false}*/}
              {/*/>*/}
            </Tab>
          ) : null}

          {user?.role === "USER" && isAdminRole(currentUserRole) ? (
            <Tab eventKey="invoices" title="Invoices">
              <InvoiceTable defaultUser={user} rowsOnly={true} />
            </Tab>
          ) : null}

          {user?.role === "USER" ? (
            <Tab eventKey="cancels" title="Shift Cancellations">
              <UserShiftCancels user={user} />
            </Tab>
          ) : null}

          {user?.role === "USER" ? (
            <Tab eventKey="availability" title={"Availability"}>
              <AvailabilityTable selectedUser={user} />
            </Tab>
          ) : null}

          {user?.role === "USER" ? (
            <Tab eventKey={"notes"} title={"Notes"}>
              <UserNotes user={user} />
            </Tab>
          ) : null}

          <Tab eventKey={"references"} title={"References"}>
            <UserReferences user={user} />
          </Tab>
        </Tabs>
      </div>

      <UpdatePasswordModal
        visible={showUpdatePassword}
        close={() => setShowUpdatePassword(false)}
        user={user}
      />
      <PlaceOnHoldModal
        visible={showHoldUser}
        close={() => setShowHoldUser(false)}
        user={user}
        updateUser={setUser}
      />
      <UserCreateUpdateModal
        visible={showUserEdit}
        close={() => setShowUserEdit(false)}
        user={user}
        updateUser={setUser}
      />
      <UserProfessionalRoleSelectionModal
        visible={showRolePrompt}
        close={closeRolePrompt}
        user={user}
        updateUser={setUser}
      />
    </div>
  );
};

export default UserDetail;
